import type { Terminal } from '../terminal';
import type { GameState, StrikeResult } from '../../types/game-state';
import type { AIDecision } from '../../types/ai';
import { getTotalPopulation } from '../../engine/world-data';
import { getWeaponCount } from '../../engine/arsenal';
import { getDefconDescription } from '../../engine/defcon';

async function runAssessment(
  terminal: Terminal,
  state: GameState,
  _playerResults: readonly StrikeResult[],
  aiResults: readonly StrikeResult[],
  aiDecision: AIDecision,
): Promise<void> {
  await terminal.println('');
  await terminal.println('════════════════════════════════════════════════════════════════');
  await terminal.println('  DAMAGE ASSESSMENT', { cssClass: 'warning' });
  await terminal.println('════════════════════════════════════════════════════════════════');
  await terminal.println('');

  // Enemy retaliation
  if (aiDecision.shouldStrike && aiResults.length > 0) {
    await terminal.println('  ┌─ ENEMY RETALIATION ─┐', { cssClass: 'danger' });
    await terminal.println('');
    await terminal.println(`  WOPR ANALYSIS:`, { cssClass: 'warning' });
    for (const line of aiDecision.reasoning.split('\n')) {
      await terminal.println(`    ${line}`, { speed: 15 });
    }
    await terminal.println('');

    await terminal.println('  INCOMING STRIKES:', { cssClass: 'danger' });
    const aiUniqueTargets = new Map<string, StrikeResult>();
    for (const result of aiResults) {
      const existing = aiUniqueTargets.get(result.targetId);
      if (!existing || result.casualties > existing.casualties) {
        aiUniqueTargets.set(result.targetId, result);
      }
    }

    for (const result of aiUniqueTargets.values()) {
      const status = result.targetDestroyed ? 'DESTROYED' : 'DAMAGED';
      const cls = result.targetDestroyed ? 'danger' : 'warning';
      await terminal.println(`    ${result.targetName.padEnd(24)} ${status}`, { cssClass: cls, speed: 10 });
      if (result.casualties > 0) {
        await terminal.println(`      CASUALTIES: ${result.casualties.toLocaleString()}`, { cssClass: 'danger', speed: 10 });
      }
      await terminal.pause(100);
    }

    const aiTotalCasualties = aiResults.reduce((sum, r) => sum + r.casualties, 0);
    if (aiTotalCasualties > 0) {
      await terminal.println('');
      await terminal.println(`  TOTAL ENEMY STRIKE CASUALTIES: ${aiTotalCasualties.toLocaleString()}`, { cssClass: 'danger' });
    }
  } else {
    await terminal.println('  ENEMY RESPONSE: NONE', { cssClass: 'bright' });
    if (aiDecision.reasoning) {
      await terminal.println(`  ANALYSIS: ${aiDecision.reasoning}`, { cssClass: 'dim' });
    }
  }

  await terminal.println('');

  // Current situation
  const usPop = getTotalPopulation(state.world.usCities);
  const ussrPop = getTotalPopulation(state.world.ussrCities);
  const origUsPop = state.world.usCities.reduce((s, c) => s + c.population, 0);
  const origUssrPop = state.world.ussrCities.reduce((s, c) => s + c.population, 0);

  const usArsenal = getWeaponCount(state.usArsenal);
  const ussrArsenal = getWeaponCount(state.ussrArsenal);

  await terminal.println('  ┌─ SITUATION REPORT ─┐', { cssClass: 'bright' });
  await terminal.println('');
  await terminal.println(`  DEFCON ${state.defconLevel}: ${getDefconDescription(state.defconLevel)}`);
  await terminal.println(`  ROUND: ${state.currentRound} OF ${state.maxRounds}`);
  await terminal.println('');
  await terminal.println('                      UNITED STATES    SOVIET UNION');
  await terminal.println('  ──────────────────────────────────────────────────');

  const usPopPct = ((usPop / origUsPop) * 100).toFixed(1);
  const ussrPopPct = ((ussrPop / origUssrPop) * 100).toFixed(1);
  await terminal.println(`  POPULATION       ${usPop.toLocaleString().padStart(13)}  ${ussrPop.toLocaleString().padStart(13)}`);
  await terminal.println(`  SURVIVING            ${usPopPct.padStart(6)}%        ${ussrPopPct.padStart(6)}%`);
  await terminal.println(`  WEAPONS READY    ${String(usArsenal.ready).padStart(13)}  ${String(ussrArsenal.ready).padStart(13)}`);
  await terminal.println(`  WARHEADS         ${String(usArsenal.warheads).padStart(13)}  ${String(ussrArsenal.warheads).padStart(13)}`);
  await terminal.println('');

  // Destroyed cities
  const destroyedUSCities = state.world.usCities.filter(c => c.isDestroyed);
  const destroyedUSSRCities = state.world.ussrCities.filter(c => c.isDestroyed);

  if (destroyedUSCities.length > 0 || destroyedUSSRCities.length > 0) {
    await terminal.println('  CITIES DESTROYED:', { cssClass: 'danger' });
    for (const city of destroyedUSCities) {
      await terminal.println(`    US: ${city.name}`, { cssClass: 'danger', speed: 10 });
    }
    for (const city of destroyedUSSRCities) {
      await terminal.println(`    USSR: ${city.name}`, { cssClass: 'danger', speed: 10 });
    }
    await terminal.println('');
  }

  await terminal.println('════════════════════════════════════════════════════════════════');
  await terminal.println('');
}

export { runAssessment };
